export interface TierPrice {
    tier: string;
    price: string | number;
}

export interface TierInfo {
    name: string;
    color: string;
    seatCount: number;
    basePrice?: number;
}

export interface TierTicketStat {
    ticketId: string;
    sold: number;
    capacity: number;
    revenue: number;
}

export interface TierPriceTicket {
    _id: string;
    name: string;
    tierPrices?: TierPrice[];
}

export interface GroupTotals {
    sold: number;
    capacity: number;
    revenue: number;
}

export interface GroupRow extends GroupTotals {
    ticketId: string;
    ticketName: string;
    tier: string;
    price: number;
}

export interface Group {
    key: string;
    label: string;
    color?: string;
    rows: GroupRow[];
    totals: GroupTotals;
    minPrice: number;
    maxPrice: number;
}

export type StatLookup = (ticketId: string, tier: string) => TierTicketStat | undefined;

const toNumber = (value: string | number | undefined | null): number => {
    if (value === undefined || value === null || value === '') return NaN;
    return Number(value);
};

export const priceRange = (tierPrices: TierPrice[] | undefined): { min: number; max: number } | null => {
    const prices = (tierPrices ?? []).map(tp => toNumber(tp.price)).filter(n => Number.isFinite(n));
    if (prices.length === 0) return null;
    return { min: Math.min(...prices), max: Math.max(...prices) };
};

export const formatRange = (
    tierPrices: TierPrice[] | undefined,
    format: (n: number) => string,
    freeLabel = 'Free',
): string => {
    const range = priceRange(tierPrices);
    if (!range) return '';
    if (range.min === range.max) return range.min === 0 ? freeLabel : format(range.min);
    if (range.min === 0) return `${freeLabel} to ${format(range.max)}`;
    return `${format(range.min)} - ${format(range.max)}`;
};

export const sumTotals = (rows: GroupTotals[]): GroupTotals =>
    rows.reduce(
        (acc, r) => ({ sold: acc.sold + r.sold, capacity: acc.capacity + r.capacity, revenue: acc.revenue + r.revenue }),
        { sold: 0, capacity: 0, revenue: 0 },
    );

const buildRow = (ticket: TierPriceTicket, tp: TierPrice, stat: StatLookup): GroupRow => {
    const s = stat(ticket._id, tp.tier);
    return {
        ticketId: ticket._id,
        ticketName: ticket.name,
        tier: tp.tier,
        price: toNumber(tp.price),
        sold: s?.sold ?? 0,
        capacity: s?.capacity ?? 0,
        revenue: s?.revenue ?? 0,
    };
};

const toGroup = (key: string, label: string, rows: GroupRow[], color?: string): Group => {
    const prices = rows.map(r => r.price).filter(n => Number.isFinite(n));
    return {
        key,
        label,
        color,
        rows,
        totals: sumTotals(rows),
        minPrice: prices.length ? Math.min(...prices) : 0,
        maxPrice: prices.length ? Math.max(...prices) : 0,
    };
};

export const groupByTier = (tickets: TierPriceTicket[], tiers: TierInfo[], stat: StatLookup): Group[] =>
    tiers.map(tier => {
        const rows = tickets.flatMap(t =>
            (t.tierPrices ?? []).filter(tp => tp.tier === tier.name).map(tp => buildRow(t, tp, stat)),
        );
        return toGroup(tier.name, tier.name, rows, tier.color);
    });

export const groupByTicket = (tickets: TierPriceTicket[], stat: StatLookup): Group[] =>
    tickets.map(t => toGroup(t._id, t.name, (t.tierPrices ?? []).map(tp => buildRow(t, tp, stat))));

export const sellableSeatCapacity = (tiers: TierInfo[], heldByTier: Record<string, number> = {}): number =>
    tiers.reduce((sum, t) => sum + Math.max(0, t.seatCount - (heldByTier[t.name] ?? 0)), 0);

export interface TierPriceValidation {
    valid: boolean;
    errors: Record<string, string>;
    formError?: string;
}

export const validateTierPrices = (
    selected: TierPrice[],
    options: { step?: number; requireTier?: boolean } = {},
): TierPriceValidation => {
    const errors: Record<string, string> = {};
    const requireTier = options.requireTier ?? true;
    if (requireTier && selected.length === 0) {
        return { valid: false, errors, formError: 'Select at least one tier' };
    }
    selected.forEach(tp => {
        const raw = typeof tp.price === 'string' ? tp.price.trim() : tp.price;
        if (raw === '' || raw === undefined || raw === null) {
            errors[tp.tier] = 'Enter a price';
            return;
        }
        const n = Number(raw);
        if (!Number.isFinite(n)) errors[tp.tier] = 'Enter a valid price';
        else if (n < 0) errors[tp.tier] = 'Price cannot be negative';
        else if (options.step && Math.abs(Math.round(n / options.step) * options.step - n) > 1e-9) {
            errors[tp.tier] = `Price must be a multiple of ${options.step}`;
        }
    });
    return { valid: Object.keys(errors).length === 0, errors };
};

export const applySamePrice = (tiers: string[], price: string): TierPrice[] =>
    tiers.map(tier => ({ tier, price }));

export const tierPriceFor = (
    tierPrices: TierPrice[] | undefined,
    tier: string | undefined,
): number | null => {
    if (!tier) return null;
    const found = (tierPrices ?? []).find(tp => tp.tier === tier);
    if (!found) return null;
    const n = toNumber(found.price);
    return Number.isFinite(n) ? n : null;
};

export const tiersFromSeatMap = (seatMap: any): TierInfo[] => {
    const categories: Array<{ name: string; color: string; basePrice?: number }> = (seatMap?.categories ?? [])
        .filter((c: any) => c?.name && c?.color)
        .map((c: any) => {
            const raw = c.basePrice;
            const base = raw === undefined || raw === null || raw === '' ? NaN : Number(raw);
            return { name: String(c.name), color: String(c.color), ...(Number.isFinite(base) && base >= 0 ? { basePrice: base } : {}) };
        });
    const counts: Record<string, number> = {};
    (seatMap?.zones ?? []).forEach((zone: any) =>
        (zone?.rows ?? []).forEach((row: any) =>
            (row?.seats ?? []).forEach((seat: any) => {
                if (seat?.category) counts[seat.category] = (counts[seat.category] ?? 0) + 1;
            })));
    return categories.map(c => ({ ...c, seatCount: counts[c.name] ?? 0 }));
};

const hasOverride = (price: string | number | undefined | null): boolean =>
    !(price === undefined || price === null || (typeof price === 'string' && price.trim() === ''));

export const effectiveTierPrice = (tier: TierInfo | undefined, overrides: TierPrice[], name: string): number | null => {
    const found = overrides.find(o => o.tier === name);
    if (found && hasOverride(found.price)) {
        const n = Number(found.price);
        return Number.isFinite(n) && n >= 0 ? n : null;
    }
    return tier?.basePrice !== undefined ? tier.basePrice : null;
};

export interface TierSelectionValidation {
    valid: boolean;
    errors: Record<string, string>;
    formError?: string;
}

export const validateTierSelection = (
    tiers: TierInfo[],
    tierIds: string[],
    overrides: TierPrice[],
    options: { step?: number } = {},
): TierSelectionValidation => {
    const errors: Record<string, string> = {};
    if (tierIds.length === 0) return { valid: false, errors, formError: 'Select at least one tier' };
    tierIds.forEach(name => {
        const found = overrides.find(o => o.tier === name);
        if (found && hasOverride(found.price)) {
            const n = Number(found.price);
            if (!Number.isFinite(n)) errors[name] = 'Enter a valid price';
            else if (n < 0) errors[name] = 'Price cannot be negative';
            else if (options.step && Math.abs(Math.round(n / options.step) * options.step - n) > 1e-9) errors[name] = `Price must be a multiple of ${options.step}`;
            return;
        }
        if (tiers.find(t => t.name === name)?.basePrice === undefined) {
            errors[name] = 'Price unavailable. Set a tier base price in the venue map or enter a price here.';
        }
    });
    return { valid: Object.keys(errors).length === 0, errors };
};

export const nonEmptyOverrides = (overrides: TierPrice[], tierIds: string[]): TierPrice[] =>
    overrides.filter(o => tierIds.includes(o.tier) && hasOverride(o.price)).map(o => ({ tier: o.tier, price: String(o.price).trim() }));

export const minEffectivePrice = (tiers: TierInfo[], tierIds: string[], overrides: TierPrice[]): number => {
    const prices = tierIds.map(name => effectiveTierPrice(tiers.find(t => t.name === name), overrides, name)).filter((n): n is number => n !== null);
    return prices.length ? Math.min(...prices) : 0;
};

export interface SeatOptionTicket {
    _id: string;
    name?: string;
    status?: boolean;
    remained?: number;
    saleStart?: string;
    saleEnd?: string;
    tierIds?: string[];
    tierPrices?: TierPrice[];
}

export interface SeatOption {
    ticketId: string;
    name: string;
    price: number;
}

export type SeatOptionExclusion = 'inactive' | 'not_mapped' | 'not_started' | 'ended' | 'sold_out' | 'no_price';

export const ticketTierNames = (ticket: SeatOptionTicket): string[] =>
    Array.from(new Set<string>([...(ticket.tierIds ?? []), ...(ticket.tierPrices ?? []).map(p => p.tier)].filter(Boolean)));

export const zonedToEpoch = (value: string | undefined, timeZone?: string): number | null => {
    if (!value) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(String(value).trim());
    if (!m) {
        const t = Date.parse(String(value));
        return Number.isNaN(t) ? null : t;
    }
    const [y, mo, d, h = '0', mi = '0'] = m.slice(1);
    const asUtc = Date.UTC(+y, +mo - 1, +d, +h, +mi);
    if (!timeZone) return asUtc;
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
        }).formatToParts(new Date(asUtc));
        const get = (type: string) => Number(parts.find(p => p.type === type)?.value);
        const shown = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
        return asUtc - (shown - asUtc);
    } catch {
        return asUtc;
    }
};

export const saleStateOf = (ticket: SeatOptionTicket, now: number = Date.now(), timeZone?: string): 'on_sale' | 'not_started' | 'ended' => {
    const start = zonedToEpoch(ticket.saleStart, timeZone);
    const end = zonedToEpoch(ticket.saleEnd, timeZone);
    if (start !== null && now < start) return 'not_started';
    if (end !== null && now >= end) return 'ended';
    return 'on_sale';
};

export const seatTicketOptions = (
    tickets: SeatOptionTicket[],
    tiers: TierInfo[],
    seatTier: string | undefined,
    now: number = Date.now(),
    timeZone?: string,
): { options: SeatOption[]; excluded: Array<{ ticketId: string; name: string; reason: SeatOptionExclusion }> } => {
    const options: SeatOption[] = [];
    const excluded: Array<{ ticketId: string; name: string; reason: SeatOptionExclusion }> = [];
    if (!seatTier) return { options, excluded };
    const tier = tiers.find(t => t.name === seatTier);
    tickets.forEach(t => {
        if (!t._id || ticketTierNames(t).length === 0) return;
        const base = { ticketId: t._id, name: t.name ?? 'Ticket' };
        if (t.status === false) return excluded.push({ ...base, reason: 'inactive' });
        if (!ticketTierNames(t).includes(seatTier)) return excluded.push({ ...base, reason: 'not_mapped' });
        const state = saleStateOf(t, now, timeZone);
        if (state !== 'on_sale') return excluded.push({ ...base, reason: state });
        if ((t.remained ?? 0) <= 0) return excluded.push({ ...base, reason: 'sold_out' });
        const price = effectiveTierPrice(tier, t.tierPrices ?? [], seatTier);
        if (price === null) return excluded.push({ ...base, reason: 'no_price' });
        options.push({ ...base, price });
    });
    return { options, excluded };
};
